import type { Card } from "../types/card";
import type { AIDifficulty, GameState, PlayerState, TableStack } from "../types/game";
import {
  buildDeckPool,
  cardsPerPlayer,
  MIN_CARDS_PER_PLAYER,
  minDeckSizeForPlayerCount,
  normalizeDeckConfig,
  supportsPlayerCount,
  type DeckConfig,
  type DeckSize,
  type WildConfig,
} from "./deck";
import { normalizeHouseRules, starfallApplies, tableHouseRules, type HouseRules } from "./houseRules";
import { newStarfallState } from "./starfall";
import { mercyApplies, newMercyState } from "./mercy";
import { burningDeckApplies, newBurningDeckState } from "./burningDeck";
import { droughtApplies, newDroughtState } from "./drought";
import { shuffle, type RNG } from "./rng";

export interface PlayerSetupInfo {
  id: string;
  nickname: string;
  isAI?: boolean;
  aiDifficulty?: AIDifficulty;
  aiSeeksStars?: boolean;
}

export class InvalidSetupError extends Error {}

export interface SetupGameOptions {
  id: string;
  players: PlayerSetupInfo[];
  deckConfig: DeckConfig;
  /** Optional table rules. Omitted means each one at its own default (`DEFAULT_HOUSE_RULES`). */
  houseRules?: HouseRules;
  rng: RNG;
  turnTimerSeconds?: number | null;
  /** 1-based round number within the ongoing match. Defaults to 1. */
  roundNumber?: number;
}

/**
 * Validates a deck config against a player count. Deck size is player-count
 * independent (design doc "Setup"), with one guard rail: every player must be
 * dealt at least MIN_CARDS_PER_PLAYER cards, or the round is over before it
 * meaningfully starts.
 */
export function validateSetup(deckConfig: DeckConfig, playerCount: number): void {
  if (playerCount < 1 || playerCount > 8) {
    throw new InvalidSetupError("Five Out supports 1-8 players");
  }
  if (!supportsPlayerCount(deckConfig, playerCount)) {
    const min = minDeckSizeForPlayerCount(playerCount, deckConfig.wilds);
    throw new InvalidSetupError(
      `${playerCount} players need at least ${MIN_CARDS_PER_PLAYER} cards each; ` +
        `this deck deals ${cardsPerPlayer(deckConfig, playerCount)}. Use ${min} deck(s) or more.`,
    );
  }
}

/** The smallest deck size that can seat this table, so UIs can grey out the rest. */
export function minimumDeckSize(playerCount: number, wilds: WildConfig): DeckSize {
  return minDeckSizeForPlayerCount(playerCount, wilds);
}

/**
 * Deals a new round: 5 face-up cards form the "+" table layout, remaining
 * cards are split evenly across players (in play order), and any leftover
 * (non-even remainder) cards are placed randomly on top of the 5 table
 * stacks, per the design doc's "Setup" section.
 */
export function setupGame(opts: SetupGameOptions): GameState {
  const deckConfig: DeckConfig = normalizeDeckConfig(opts.deckConfig);
  validateSetup(deckConfig, opts.players.length);
  return dealPool(opts, deckConfig, shuffle(buildDeckPool(deckConfig, opts.rng), opts.rng), tableHouseRules(opts.houseRules));
}

/**
 * Options for a round dealt from an explicit pool of cards rather than from a
 * deck size: the campaign's small decks (a 10-card pool is "a 5-card deck"
 * solo). `deckConfig` is still required, because a round carries one, and its
 * wilds should describe the pool's; its size is not read.
 */
export interface PoolSetupOptions extends Omit<SetupGameOptions, "deckConfig"> {
  pool: Card[];
  deckConfig: DeckConfig;
}

/**
 * Deals a round from exactly these cards, shuffled by the round's generator,
 * the way `setupGame` deals a deck: five to the table, the rest split evenly
 * and the remainder onto random piles.
 *
 * LOCAL ONLY, with no Go counterpart: the campaign is single-player and never
 * reaches the server, and nothing here is a rule - every move on the round it
 * deals is judged by the same engine as any other. The deck-size guard rail is
 * replaced by the only floor dealing itself needs: five table cards and a card
 * for every seat.
 *
 * The house rules are taken AS GIVEN (`normalizeHouseRules`), not through
 * `tableHouseRules`, which forces Vinny's Rule and Streaks on at the doors a
 * HOST's choice comes in by. A campaign level is not a host's table: its early
 * levels switch rules off so that each one arrives on its own.
 */
export function setupGameFromPool(opts: PoolSetupOptions): GameState {
  const deckConfig: DeckConfig = normalizeDeckConfig(opts.deckConfig);
  if (opts.players.length < 1 || opts.players.length > 8) throw new InvalidSetupError("Five Out supports 1-8 players");
  if (opts.pool.length < 5 + opts.players.length) {
    throw new InvalidSetupError(`a pool of ${opts.pool.length} cannot deal 5 piles and a card to each of ${opts.players.length} players`);
  }
  return dealPool(opts, deckConfig, shuffle([...opts.pool], opts.rng), normalizeHouseRules(opts.houseRules));
}

/** Options for a round whose deal is given card for card: every pile and every deck, top card first. */
export interface FixedDealOptions extends Omit<SetupGameOptions, "deckConfig"> {
  deckConfig: DeckConfig;
  /** The five piles, each top card first. */
  stacks: Card[][];
  /** One deck per player, in seat order, each top card first. */
  decks: Card[][];
}

/**
 * Deals exactly the given table and decks: a campaign level that is the same
 * deal on every attempt. Stored as cards rather than as a seed because a seed
 * only reproduces a deal while `buildDeckPool` and `shuffle` never change
 * (docs/CAMPAIGN_FEASIBILITY.md, section 4). The generator still drives
 * everything after the deal - CPU choices and the house rules' own dice - so a
 * player who repeats their moves sees the same round.
 *
 * LOCAL ONLY, like `setupGameFromPool`.
 */
export function setupFixedGame(opts: FixedDealOptions): GameState {
  const deckConfig: DeckConfig = normalizeDeckConfig(opts.deckConfig);
  if (opts.stacks.length !== 5 || opts.stacks.some((s) => s.length === 0)) {
    throw new InvalidSetupError("a fixed deal needs five piles with a card on each");
  }
  if (opts.decks.length !== opts.players.length || opts.decks.some((d) => d.length === 0)) {
    throw new InvalidSetupError("a fixed deal needs a non-empty deck for every player");
  }
  const stacks: TableStack[] = opts.stacks.map((cards) => ({ cards: [...cards] }));
  const dealt = Math.min(...opts.decks.map((d) => d.length));
  return assembleRound(opts, deckConfig, normalizeHouseRules(opts.houseRules), stacks, opts.decks.map((d) => [...d]), dealt);
}

/** The deal itself, from an already-shuffled pool: shared by every way of dealing from a pool. */
function dealPool(opts: Omit<SetupGameOptions, "deckConfig">, deckConfig: DeckConfig, pool: Card[], houseRules: HouseRules): GameState {
  const { players, rng } = opts;

  const stacks: TableStack[] = Array.from({ length: 5 }, () => ({ cards: [] as Card[] }));
  for (let i = 0; i < 5; i++) {
    stacks[i].cards.push(pool[i]);
  }
  const remaining = pool.slice(5);

  const perPlayer = Math.floor(remaining.length / players.length);
  const remainderCount = remaining.length - perPlayer * players.length;
  const decks = players.map((_, idx) => remaining.slice(idx * perPlayer, (idx + 1) * perPlayer));

  // Leftover cards (remainder when it doesn't divide evenly) go randomly onto the 5 table stacks.
  const leftovers = remaining.slice(perPlayer * players.length);
  for (const card of leftovers) {
    const target = rng.nextInt(5);
    stacks[target].cards.unshift(card); // becomes the new top of that stack
  }

  if (remainderCount !== leftovers.length) {
    throw new Error("internal error: remainder accounting mismatch");
  }

  return assembleRound(opts, deckConfig, houseRules, stacks, decks, perPlayer);
}

/**
 * A dealt table and decks made into a round: the seats, and the house rules'
 * own state. Called once the deal has finished drawing from the generator,
 * which is what the house rules' seeds are read from.
 */
function assembleRound(
  opts: Omit<SetupGameOptions, "deckConfig">,
  deckConfig: DeckConfig,
  houseRules: HouseRules,
  stacks: TableStack[],
  decks: Card[][],
  perPlayer: number,
): GameState {
  const { id, players, rng } = opts;
  const playerStates: PlayerState[] = players.map((p, idx) => ({
    id: p.id,
    nickname: p.nickname,
    isAI: !!p.isAI,
    aiDifficulty: p.aiDifficulty,
    ...(p.aiSeeksStars ? { aiSeeksStars: true } : {}),
    connected: true,
    deck: decks[idx],
    turnsTaken: 0,
    streak: 0,
  }));

  // Seeded off the round's generator WITHOUT drawing from it, so the deal and
  // every later AI decision are exactly what they were before Starfall existed.
  const starfallSeed = (Math.imul((rng.seed ^ 0x9e3779b9) >>> 0, 0x85ebca6b) + rng.steps) >>> 0;
  const starfall = starfallApplies(houseRules) ? newStarfallState(starfallSeed, stacks) : undefined;
  // The Mercy Rule's own generator, spread from the same material so it never
  // draws from the deal's either, and never shares a stream with Starfall's.
  // It keeps the deal too: its line is a multiple of what each seat was dealt.
  const mercy = mercyApplies(houseRules, players.length)
    ? newMercyState(Math.imul((starfallSeed ^ 0x2545f491) >>> 0, 0x9e3779b1), perPlayer)
    : undefined;
  // Drought's, the same way, on a stream of its own.
  const drought = droughtApplies(houseRules, players.length)
    ? newDroughtState(Math.imul((starfallSeed ^ 0x6a09e667) >>> 0, 0x85ebca77))
    : undefined;
  // Burning Deck keeps a tally only, so it has no generator to seed.
  const burningDeck = burningDeckApplies(houseRules, players.length) ? newBurningDeckState() : undefined;

  return {
    id,
    phase: "playing",
    deckConfig,
    houseRules,
    ...(starfall ? { starfall } : {}),
    ...(mercy ? { mercy } : {}),
    ...(drought ? { drought } : {}),
    ...(burningDeck ? { burningDeck } : {}),
    players: playerStates,
    currentPlayerIndex: 0,
    stacks: stacks as GameState["stacks"],
    turnTimerSeconds: opts.turnTimerSeconds ?? null,
    streakLocks: [],
    streakTargets: [],
    pendingDecision: null,
    winnerPlayerId: null,
    scores: null,
    cardsLeft: null,
    roundNumber: opts.roundNumber ?? 1,
    version: 1,
  };
}
